import React from 'react';
import ReactDOM from 'react-dom/client';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import App from './App';
import { captureException, initSentry } from './lib/sentry';

initSentry();

// Handle Vite code-splitting chunk preload errors after a new deployment.
// This commonly happens when a user keeps an old tab open, then navigates to a lazy-loaded page.
// We reload once to fetch the latest HTML + assets and avoid a broken state.
if (typeof window !== 'undefined') {
  const RELOAD_KEY = 'nexusos:preload_error_reload_at';
  const RELOAD_DEDUP_MS = 10_000;
  window.addEventListener('vite:preloadError', (event) => {
    try {
      event.preventDefault();
    } catch {
      // ignore
    }
    try {
      const lastReloadAt = Number(window.sessionStorage.getItem(RELOAD_KEY) || '0');
      if (Number.isFinite(lastReloadAt) && lastReloadAt > 0 && Date.now() - lastReloadAt < RELOAD_DEDUP_MS) {
        return;
      }
      window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    } catch {
      // ignore
    }
    window.location.reload();
  });
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      captureException(error, {
        source: 'react_query',
        kind: 'query',
        queryKey: query.queryKey,
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      captureException(error, {
        source: 'react_query',
        kind: 'mutation',
        mutationKey: mutation.options.mutationKey,
      });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      {import.meta.env.PROD && <Analytics />}
      {import.meta.env.PROD && <SpeedInsights />}
    </QueryClientProvider>
  </React.StrictMode>
);
